/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useMount } from '../hooks';
import { TId } from '../types';

function upper(s: string) {
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
}

type StatefulStoreUnsuscriber = () => void;

export type StatefulStoreUpdater<Props> = (currentProps: Props) => Props;

export type StatefulStoreUpdateProps<Props> =
  | StatefulStoreUpdater<Props>
  | Partial<Props>;

function isUpdater<Props>(
  props: StatefulStoreUpdater<Props> | Partial<Props>,
): props is StatefulStoreUpdater<Props> {
  return typeof props === 'function';
}

/**
 * Clase facilitadora del uso de StatefulStore. Es un pequeño almacen que
 * permite actualizar sus propiedades y sabe cómo sincronizarse con el store de
 * modo que los cambios se propaguen según corresponda.
 */
export abstract class BasicStoredElement<
  Props extends Record<string, unknown>,
> {
  abstract getId(): TId;

  #onUpdateCbs: (() => void)[] = [];
  onUpdate(cb: () => void): StatefulStoreUnsuscriber {
    this.#onUpdateCbs.push(cb);

    return () => {
      this.#onUpdateCbs = this.#onUpdateCbs.filter((current) => current !== cb);
    };
  }

  props: Props = {} as Props;
  constructor(props: Props) {
    this.props = props;
  }

  protected shoutUpdates = () => {
    this.#onUpdateCbs.forEach((current) => current());
  };

  update(newProps: StatefulStoreUpdateProps<Props>) {
    const actualNewProps = isUpdater(newProps)
      ? newProps(this.props)
      : newProps;

    this.props = { ...this.props, ...actualNewProps };
    this.shoutUpdates();
  }

  useProps = () => {
    const [props, setState] = useState(this.props);

    useMount(() => {
      setState({ ...this.props });

      return this.onUpdate(() => {
        setState({ ...this.props });
      });
    });

    return props;
  };
}

/**
 * Son las acciones de adición del store.
 */
type AddActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `add${Capitalize<string & P>}`]: (el: T[P]) => void;
};

/**
 * Acciones de vaciado del store.
 */
type EmptyActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `empty${Capitalize<string & P>}`]: () => void;
};

/**
 * Acciones de removido del store.
 */
type RemoveActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `remove${Capitalize<string & P>}`]: (id: TId) => void;
};

type UpdateListActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `update${Capitalize<string & P>}List`]: (
    newList: T[P],
  ) => void;
};

/**
 * Conjunto de acciones disponibles del store
 */
type ActionMethods<T extends Record<string, BasicStoredElement<any>>> = {
  actions: AddActions<T> &
    EmptyActions<T> &
    RemoveActions<T> &
    UpdateListActions<T>;
};

/**
 * Método que permite obtener el listado de elementos de un tipo.
 */
type GetStateActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `get${Capitalize<string & P>}State`]: () => T[P][];
};

/**
 * Método que permite conocer las propiedades de un elemento.
 */
type GetItemActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `get${Capitalize<string & P>}`]: (id: TId) => T[P];
};

type UpdateItemActions<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `update${Capitalize<string & P>}`]: (
    id: TId,
    newProps: StatefulStoreUpdateProps<T[P]['props']>,
  ) => void;
};

/**
 * Conjunto de métodos que permiten acceder al estado en un momento
 * determinado.
 */
type StateMethods<T extends Record<string, BasicStoredElement<any>>> = {
  state: GetStateActions<T> & GetItemActions<T> & UpdateItemActions<T>;
};

/**
 * Es el hook encargado de permitir la escucha de listado de elementos y sus
 * propiedades.
 */
type HookUseState<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `use${Capitalize<string & P>}`]: () => T[P][];
};

/**
 * Es el hook encargado de permitir la escucha de listado de elementos
 */
type HookUseList<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `use${Capitalize<string & P>}List`]: () => T[P][];
};

/**
 * Es el hook encargado de permitir la escucha de propiedades de un elemento
 */
type HookUseStateById<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `use${Capitalize<string & P>}ById`]: /**
   * Cada hook permite conocer el listado de elementos, reflejando la
   * actualización sobre éstos en tiempo real.
   */ (id: TId) => T[P] | undefined;
};

/**
 * Conjunto de hooks del store.
 */
type HooksMethods<T extends Record<string, BasicStoredElement<any>>> = {
  hooks: HookUseState<T> & HookUseStateById<T> & HookUseList<T>;
};

/**
 * Es la suscripción encargada de permitir la escucha de propiedades de un
 * elemento
 */
type SuscribeById<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `change${Capitalize<string & P>}Element`]: (
    id: TId,
    cb: (props: T[P]) => unknown,
  ) => StatefulStoreUnsuscriber;
};

/**
 * Es la suscripción encaragada de permitir la escucha de cambios en la lista y
 * las propiedades de sus elementos.
 */
type SuscribeToListChange<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `change${Capitalize<string & P>}`]: (
    cb: (props: T[P][]) => unknown,
  ) => StatefulStoreUnsuscriber;
};

/**
 * Es la suscripción encaragada de permitir la escucha de cambios en la
 * cantidad de elementos de la lista, pero no en las propiedades de los
 * elementos.
 */
type SuscribeToListCountChange<
  T extends Record<string, BasicStoredElement<any>>,
> = {
  [P in keyof T as `change${Capitalize<string & P>}Ammount`]: (
    cb: (props: T[P][]) => unknown,
  ) => StatefulStoreUnsuscriber;
};

type SuscribeMethods<T extends Record<string, BasicStoredElement<any>>> = {
  on: SuscribeById<T> & SuscribeToListChange<T> & SuscribeToListCountChange<T>;
};

type InitialStates<T extends Record<string, BasicStoredElement<any>>> = {
  [P in keyof T as `${string & P}State`]: T[P][];
};
type TPeople = { name: string };
class People extends BasicStoredElement<TPeople> {
  getId(): TId {
    return this.props.name;
  }
}

const r = makeStatefulStore({
  people: People.prototype,
});

r.actions.addPeople;
r.actions.emptyPeople;
r.actions.removePeople;
r.actions.updatePeopleList;
r.hooks.usePeople;
r.hooks.usePeopleById;
r.hooks.usePeopleList;
r.on.changePeople;
r.on.changePeopleAmmount;
r.on.changePeopleElement;
r.state.getPeople;
r.state.getPeopleState;
r.state.updatePeople;

/**
 * Este store permite mantener `N` listados de objetos y por cada tipo de
 * objeto ofrece métodos para agregar, eliminar, obtener uno, obtener todos y
 * un hook para conocer el listado en tiempo real.
 * 
 * **Métodos disponibles**
 * 
 *  - **action.addXXX**: Agrega un elemento de tipo XXX a la lista.
 *  - **action.emptyXXX**: Vacía el listado de tipo XXX.
 *  - **action.removeXXX**: Elimina un elemento de tipo XXX de la lista.
 *  - **action.updateXXXList**: Permite actualizar toda la lista en una sola
 *  acción. - **hooks.useXXX**: Permite conocer el listado de elementos y el
 *  cambio
 *  en sus propiedades en tiempo real. 
 *  - **hooks.useXXXById**: Permite conocer los cambios a un elemento de la
 *  lista en tiempo real. 
 *  - **hooks.useXXXList**: Permite conocer el listado de
 *  elementos pero no reacciona a los cambios en las propiedades de los
 *  elementos. 
 *  - **on.changeXXX**: Permite suscribirse al listado de elementos y
 *  cambios en sus propiedades.
 *  - **on.changeXXXAmmount**: Permite suscribirse
 *  al listado de elementos, sin tomar en cuenta el cambio en las propiedades
 *  de sus items.
 *  - **on.changeXXXElement**: Permite suscribirse al cambio de
 *  propiedades de un ítem particular
 *  - **state.getXXX**: Permite obtener el
 *  esatdo de un elemento particular de la lista.
 *  - **state.getXXXState**:
 *  Permite obtener el estado del listado completo de un tipo de elementos.
 *  - **state.updateXXX**: Es un atajo para state.getXXX(id).update
 * 
 * La funcionalidad de actualización se implementa en el objeto almacenado y es
 * comunicada en forma automática al store, de forma que el store no conoce
 * solamente cuántos y cuáles elementos hay almacenados, sino que además refleja
 * en tiempo real el estado de sus propiedades.
 * 
 * @example
 * 
    type TPeople = { name: string };
    class People extends BasicStoredElement<TPeople> {
      getId(): TId {
        return this.props.name;
      }
    }

    class Schedule extends BasicStoredElement<{ day: string; task: string }>
    {
      getId() {
        return this.props.day;
      }
    }

    const r = makeStatefulStore(
      {
        people: People.prototype,
        schedules: Schedule.prototype,
      },
      {
        peopleState: [new People({ name: 'A' }), new People({ name: 'B' })],
      },
    );

    const p = r.hooks.usePeople();
    p[0].props.name;

    r.hooks.usePeople();

    r.actions.removePeople('asdf');

    r.actions.addPeople(new People({ name: 'asdf' }));
    r.actions.removePeople('asdf');

    r.state.getPeopleState();
    const pep = r.state.getPeople('asdf')

    const ph = r.hooks.usePeople();
    const sch = r.hooks.useSchedules();
 */
export function makeStatefulStore<
  T extends Record<string, BasicStoredElement<any>>,
>(entries: T, initialStates?: Partial<InitialStates<T>>) {
  const hookUseState = {} as HookUseState<T>;
  const hookUseList = {} as HookUseList<T>;
  const hookUseStateById = {} as HookUseStateById<T>;

  const suscribeById = {} as SuscribeById<T>;
  const suscribeToList = {} as SuscribeToListChange<T>;
  const suscribeToListCount = {} as SuscribeToListCountChange<T>;

  const addActions = {} as AddActions<T>;
  const emptyActions = {} as EmptyActions<T>;
  const removeActions = {} as RemoveActions<T>;
  const updateListActions = {} as UpdateListActions<T>;

  const getItemActions = {} as GetItemActions<T>;
  const getStateActions = {} as GetStateActions<T>;
  const updateItemActions = {} as UpdateItemActions<T>;

  Object.keys(entries).forEach((name) => {
    /**
     * Make store
     */
    const initialStateKey = `${upper(name)}State` as keyof InitialStates<T>;
    let store = {} as Record<string, T[typeof name]>;
    const initialState = initialStates?.[initialStateKey] as T[typeof name][];
    initialState?.forEach((current) => {
      store[current.getId()] = current;
    });

    /** Make event handlers */
    let listStateChangeHandlers: (() => void)[] = [];
    /**
     * Este método avisa acerca de un cambio en la cantidad de elementos o de
     * un cambio en las propiedades de algún elemento.
     */
    function shoutListStateChange() {
      listStateChangeHandlers.forEach((current) => current());
    }

    const elementChangeHandlers: Record<TId, (() => void)[]> = {};
    /**
     * Este método avisa acerca de un cambio en las propiedades de un elemento.
     */
    function shoutListElementChange(id: TId) {
      elementChangeHandlers[id]?.forEach((current) => current());
    }

    let listCountChangeHandlers: (() => void)[] = [];
    /**
     * Este método avisa acerca de un cambio en la cantidad de elementos
     */
    function shoutListCountChange() {
      listCountChangeHandlers.forEach((current) => current());
    }

    const onUpdateElementSuscriptions: Record<string, () => void> = {};

    /**
     *
     * Make actions
     *
     **/

    const addKey = `add${upper(name)}` as keyof AddActions<T>;
    /**
     * La acción de agregado del store. Agrega elementos y notifica tanto a los
     * listeners específicos del elemento como a los que escuchan el listado de
     * elementos.
     */
    addActions[addKey] = ((newElement: T[typeof name]) => {
      let elId = newElement.getId();
      let lastProps = { ...newElement.props };
      store[elId] = newElement as T[typeof name];
      const suscription = newElement.onUpdate(() => {
        const id = newElement.getId();

        if (id !== elId) {
          if (store[id]) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            newElement.update(lastProps);
            throw new Error(
              `Cannot set new id ${id} because it already exists`,
            );
          }

          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete store[elId];
          store[id] = newElement;

          elId = id;
          lastProps = newElement.props;
        }

        shoutListElementChange(id);
        shoutListStateChange();
      });
      onUpdateElementSuscriptions[newElement.getId()] = suscription;
      shoutListStateChange();
      shoutListCountChange();
      shoutListElementChange(elId);
    }) as AddActions<T>[typeof addKey];

    /**
     * Acción de borrado completo. Vacía el store y avisa a todos los
     * listeners, incluyendo los que escuchan las propiedades de un elemento
     * particular.
     */
    const emptyKey = `empty${upper(name)}` as keyof EmptyActions<T>;
    emptyActions[emptyKey] = (() => {
      Object.values(onUpdateElementSuscriptions).forEach((current) =>
        current(),
      );
      store = {};
      shoutListCountChange();
      shoutListStateChange();
      Object.keys(elementChangeHandlers).forEach((current) =>
        shoutListElementChange(current),
      );
    }) as EmptyActions<T>[typeof emptyKey];

    /**
     * Elimina un elemento y avisa al store general y particular.
     */
    const removeKey = `remove${upper(name)}` as keyof RemoveActions<T>;
    removeActions[removeKey] = ((id: TId) => {
      onUpdateElementSuscriptions[id]?.();
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete store[id];
      shoutListCountChange();
      shoutListStateChange();
      shoutListElementChange(id);
    }) as RemoveActions<T>[typeof removeKey];

    const updateListKey = `update${upper(
      name,
    )}List` as keyof UpdateListActions<T>;
    updateListActions[updateListKey] = ((list: T[typeof name]) => {
      store[name] = list;
      shoutListCountChange();
      shoutListStateChange();
    }) as UpdateListActions<T>[typeof updateListKey];

    /**
     * Permite obtener el estado del listado completo de un tipo de elementos.
     */
    const getStateKey = `get${upper(name)}State` as keyof GetStateActions<T>;
    getStateActions[getStateKey] = (() => {
      return Object.values(store) as T[typeof name][];
    }) as GetStateActions<T>[typeof getStateKey];
    const getState = getStateActions[getStateKey] as () => T[typeof name][];

    /**
     * Permite obtener el estado de un elemento en particular.
     */
    const getItemKey = `get${upper(name)}` as keyof GetItemActions<T>;
    getItemActions[getItemKey] = ((id: TId) => {
      return store[id] as T[typeof name];
    }) as GetItemActions<T>[typeof getItemKey];

    /**
     * Permite actualizar un elemento
     */
    const updateItemKey = `update${upper(name)}` as keyof UpdateItemActions<T>;
    updateItemActions[updateItemKey] = ((
      id: TId,
      newProps: StatefulStoreUpdateProps<T[typeof name]>,
    ) => {
      store[id].update(newProps);
    }) as UpdateItemActions<T>[typeof updateItemKey];

    const hookUseStateKey = `use${upper(name)}` as keyof HookUseState<T>;
    /**
     * Este hook permite conocer el estado de todos los elementos del listado
     * en tiempo real.
     */
    hookUseState[hookUseStateKey] = (() => {
      const [state, setState] = useState(getState() as T[typeof name][]);

      useMount(() => {
        setState(getState() as T[typeof name][]);

        const eventHandler = () => {
          setState(getState() as T[typeof name][]);
        };

        listStateChangeHandlers.push(eventHandler);

        return () => {
          listStateChangeHandlers = listStateChangeHandlers.filter(
            (current) => current !== eventHandler,
          );
        };
      });

      return state;
    }) as unknown as HookUseState<T>[typeof hookUseStateKey];

    const hookUseListKey = `use${upper(name)}List` as keyof HookUseList<T>;
    /**
     * Este hook permite conocer el listado de elementos en tiempo real, sin
     * preocuparse por el cambio de propiedades.
     */
    hookUseList[hookUseListKey] = (() => {
      const [state, setState] = useState(getState() as T[typeof name][]);

      useMount(() => {
        setState(getState() as T[typeof name][]);

        const eventHandler = () => {
          setState(getState() as T[typeof name][]);
        };

        listCountChangeHandlers.push(eventHandler);

        return () => {
          listCountChangeHandlers = listCountChangeHandlers.filter(
            (current) => current !== eventHandler,
          );
        };
      });

      return state;
    }) as unknown as HookUseList<T>[typeof hookUseListKey];

    const hookUseStateByIdKey = `use${upper(
      name,
    )}ById` as keyof HookUseStateById<T>;

    hookUseStateById[hookUseStateByIdKey] = ((id: TId) => {
      const [state, setState] = useState({ obj: store[id] });

      useMount(() => {
        setState({ obj: store[id] });

        const eventHandler = () => {
          setState({ obj: store[id] });
        };

        if (!elementChangeHandlers[id]) elementChangeHandlers[id] = [];
        elementChangeHandlers[id].push(eventHandler);

        return () => {
          elementChangeHandlers[id] = elementChangeHandlers[id].filter(
            (current) => current !== eventHandler,
          );
        };
      });

      return state.obj;
    }) as unknown as HookUseStateById<T>[typeof hookUseStateByIdKey];
    /**
     * Esta suscripción permite conocer el estado de un único elemento en
     * tiempo real.
     */
    const onChangeElementKey = `change${upper(
      name,
    )}Element` as keyof SuscribeById<T>;
    suscribeById[onChangeElementKey] = ((
      id: string,
      cb: (props: T[typeof name]) => unknown,
    ) => {
      if (!elementChangeHandlers[id]) elementChangeHandlers[id] = [];

      const innerCb = () => {
        cb(store[id]);
      };
      elementChangeHandlers[id].push(innerCb);

      return () => {
        elementChangeHandlers[id] = elementChangeHandlers[id].filter(
          (current) => current !== innerCb,
        );
      };
    }) as SuscribeById<T>[typeof onChangeElementKey];

    const onChangeListKey = `change${upper(
      name,
    )}` as keyof SuscribeToListChange<T>;
    /**
     * Esta suscripción permite conocer el estado de la lista en tiempo real,
     * incluyendo cambios en los elementos.
     */
    suscribeToList[onChangeListKey] = ((
      cb: (props: T[typeof name][]) => unknown,
    ) => {
      const innerCb = () => {
        cb(getState());
      };
      listStateChangeHandlers.push(innerCb);

      return () => {
        listStateChangeHandlers = listStateChangeHandlers.filter(
          (current) => current !== innerCb,
        );
      };
    }) as SuscribeToListChange<T>[typeof onChangeListKey];

    const onChangeListCountKey = `change${upper(
      name,
    )}Ammount` as keyof SuscribeToListCountChange<T>;
    /**
     * Esta suscripción permite conocer el estado de la lista en tiempo real,
     * sin incluir los cambios en los elementos.
     */
    suscribeToListCount[onChangeListCountKey] = ((
      cb: (props: T[typeof name][]) => unknown,
    ) => {
      const innerCb = () => {
        cb(getState());
      };
      listCountChangeHandlers.push(innerCb);

      return () => {
        listCountChangeHandlers = listCountChangeHandlers.filter(
          (current) => current !== innerCb,
        );
      };
    }) as SuscribeToListCountChange<T>[typeof onChangeListCountKey];
  });

  const actions = Object.assign(
    {},
    addActions,
    removeActions,
    emptyActions,
    updateListActions,
  ) as ActionMethods<T>['actions'];

  const hooks = Object.assign({}, hookUseState, hookUseStateById, hookUseList);

  const on = Object.assign(
    {},
    suscribeById,
    suscribeToList,
    suscribeToListCount,
  );

  const state = Object.assign(
    {},
    getStateActions,
    getItemActions,
    updateItemActions,
  );

  return Object.assign(
    {},
    { hooks },
    { actions },
    { state },
    { on },
  ) as ActionMethods<T> &
    HooksMethods<T> &
    StateMethods<T> &
    SuscribeMethods<T>;
}
